import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getAllOverallMaterials,
  getOverallMaterialById,
} from "../helper/apicalls/overallMaterials";
import PlayCircleFilledRoundedIcon from "@mui/icons-material/PlayCircleFilledRounded";
import "../styles/MaterialFolder.scss";
import pdfImg from "../assets/images/pdfIcon.svg";
import audioIcon from "../assets/png/audioIcon.png";
import imgIcon from "../assets/png/imgIcon.png";
import powerpointIcon from "../assets/png/powerpointIcon.png";
import musicIcon from "../assets/png/musicIcon.png";
import notAvailable from "../assets/images/notAvalaible.svg";
import GlobalLoader from "../components/GlobalLoader";

const VideoIcon = () => (
  <div
    style={{
      width: 50,
      height: 60,
      backgroundColor: "#E5E5E5",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 8,
    }}
  >
    <PlayCircleFilledRoundedIcon
      style={{ color: "var(--icon-yellow-color)", fontSize: 36 }}
    />
  </div>
);

const PdfIcon = () => (
  <div
    style={{
      width: 60,
      height: 60,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 8,
    }}
  >
    <img
      src={pdfImg}
      alt="PDF"
      style={{
        width: "100%",
        height: "100%",
        objectFit: "contain",
      }}
    />
  </div>
);

const OverallMaterials = () => {
  const { coursekey } = useParams();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeMaterial, setActiveMaterial] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const [imageError, setImageError] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [pptViewer, setPptViewer] = useState("office");

  useEffect(() => {
    const fetchMaterials = async () => {
      if (!coursekey) return;

      try {
        setLoading(true);
        const res = await getAllOverallMaterials({ course_key: coursekey });
        const items = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : [];
        const formattedItems = items.map((item) => ({
          ...item,
          _kind: "material",
        }));

        setMaterials(formattedItems);
      } catch (e) {
        console.error("Failed to load overall materials", e);
        setMaterials([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMaterials();
  }, [coursekey]);

  const handleOpenMaterial = async (item) => {
    const id = item.id || item._id;
    if (!id) return;

    try {
      setModalLoading(true);
      const details = await getOverallMaterialById(id);
      const materialData = details.data || details;

      setActiveMaterial(materialData);
      setImageError(false);
      setAudioError(false);
      setPptViewer("office");
    } catch (e) {
      console.error("Failed to fetch material details", e);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setActiveMaterial(null);
  };

  const getMaterialType = (material) => {
    const t = String(material.materials_type || "").toLowerCase();
    if (t === "video" || t === "videos") return "video";
    if (t === "pdf") return "pdf";
    if (t === "audio") return "audio";
    if (t === "ppt" || t === "pptx" || t === "presentation") return "ppt";
    if (t === "image" || t === "img" || t === "images") return "image";
    return "";
  };

  const getMaterialLink = (material) => {
    return material?.content || "";
  };

  const renderMaterialIcon = (item) => {
    const type = String(item?.materials_type || "").toLowerCase();
    if (type === "pdf") {
      return <PdfIcon />;
    }
    if (type === "video" || type === "videos") {
      return <VideoIcon />;
    }
    if (type === "audio") {
      return (
        <img
          src={audioIcon}
          alt="Audio Icon"
          style={{ width: "48px", height: "48px", marginTop: "4px" }}
        />
      );
    }
    if (type === "img" || type === "image" || type === "images") {
      return (
        <img
          src={imgIcon}
          alt="Image Icon"
          style={{ width: "48px", height: "48px", marginTop: "4px" }}
        />
      );
    }
    if (
      type === "powerpoint" ||
      type === "ppt" ||
      type === "pptx" ||
      type === "presentation"
    ) {
      return (
        <img
          src={powerpointIcon}
          alt="PowerPoint Icon"
          style={{ width: "48px", height: "48px", marginTop: "4px" }}
        />
      );
    }
    return (
      <PlayCircleFilledRoundedIcon style={{ fontSize: 36, color: "#ccc" }} />
    );
  };

  return (
    <div className="materials-page">
      {loading ? (
        <GlobalLoader />
      ) : materials.length === 0 ? (
        <div className="not-found-container">
          <img
            src={notAvailable}
            alt="Not Available"
            style={{ width: "300px", height: "300px", marginTop: "4px" }}
          />
          <h3>No Guides and Materials Found!</h3>
        </div>
      ) : (
        <div className="material-folder-grid">
          {materials.map((material) => (
            <div
              key={material.id || material._id}
              className="material-folder-item"
              onClick={() => handleOpenMaterial(material)}
            >
              {renderMaterialIcon(material)}

              <div
                className="folder-name"
                title={material.name || material.title}
              >
                {material.name || material.title || "Untitled"}
              </div>
            </div>
          ))}
        </div>
      )}

      {(activeMaterial || modalLoading) && (
        <div
          className="materials-modal"
          onClick={!modalLoading ? handleCloseModal : undefined}
        >
          <div
            className="materials-modal__content"
            onClick={(e) => e.stopPropagation()}
            style={
              modalLoading
                ? {
                    minHeight: "200px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }
                : {}
            }
          >
            {modalLoading ? (
              <GlobalLoader />
            ) : (
              <>
                <div className="materials-modal__close-wrapper">
                  <div className="materials-title">
                    {activeMaterial.name || activeMaterial.title}
                  </div>
                  <button
                    type="button"
                    className="materials-modal__close"
                    onClick={handleCloseModal}
                  >
                    ×
                  </button>
                </div>
                {(() => {
                  const type = getMaterialType(activeMaterial);
                  const link = getMaterialLink(activeMaterial);
                  const name =
                    activeMaterial.name || activeMaterial.title || "";

                  const isVideo = type === "video";
                  const isPdf = type === "pdf";
                  const isAudio = type === "audio";
                  const isImage = type === "image";
                  const isPpt = type === "ppt";

                  const isYouTubeLink =
                    typeof link === "string" &&
                    (link.includes("youtube.com") || link.includes("youtu.be"));

                  let youtubeEmbedUrl = null;
                  if (isYouTubeLink) {
                    try {
                      if (link.includes("youtube.com/watch")) {
                        const url = new URL(link);
                        const v = url.searchParams.get("v");
                        if (v)
                          youtubeEmbedUrl = `https://www.youtube.com/embed/${v}`;
                      } else if (link.includes("youtu.be/")) {
                        const id = link
                          .split("youtu.be/")[1]
                          ?.split(/[?&#]/)[0];
                        if (id)
                          youtubeEmbedUrl = `https://www.youtube.com/embed/${id}`;
                      }
                    } catch (e) {
                      youtubeEmbedUrl = null;
                    }
                  }

                  return (
                    <div className="materials-modal__body">
                      {isVideo && link && youtubeEmbedUrl && (
                        <iframe
                          title={name || "Video"}
                          src={youtubeEmbedUrl}
                          style={{
                            width: "100%",
                            height: "70dvh",
                            border: "none",
                          }}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      )}

                      {isVideo && link && !youtubeEmbedUrl && (
                        <video
                          controls
                          style={{
                            width: "100%",
                            maxHeight: "70dvh",
                            borderRadius: 8,
                          }}
                        >
                          <source src={link} />
                          Your browser does not support the video tag.
                        </video>
                      )}

                      {isPdf && link && link.toLowerCase().includes(".pdf") && (
                        <iframe
                          title={name || "PDF"}
                          src={link}
                          style={{
                            width: "100%",
                            height: "70dvh",
                            border: "none",
                          }}
                        />
                      )}

                      {isPdf &&
                        link &&
                        !link.toLowerCase().includes(".pdf") && (
                          <div>
                            <p>
                              This material is an invalid link/format for PDF
                              viewer.
                            </p>
                          </div>
                        )}

                      {isAudio && link && !audioError && (
                        <>
                          <img
                            src={musicIcon}
                            alt="Audio"
                            style={{
                              width: "250px",
                              height: "250px",
                              marginBottom: "16px",
                              alignSelf: "center",
                            }}
                          />
                          <audio
                            controls
                            style={{ width: "100%" }}
                            onError={() => setAudioError(true)}
                          >
                            <source src={link} />
                            Your browser does not support the audio element.
                          </audio>
                        </>
                      )}

                      {isAudio && audioError && (
                        <div style={{ textAlign: "center", padding: "20px" }}>
                          <p>Unable to play audio.</p>
                          <a
                            href={link}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-download"
                          >
                            Download
                          </a>
                        </div>
                      )}

                      {isImage && link && !imageError && (
                        <img
                          src={link}
                          alt={name}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "70dvh",
                            objectFit: "contain",
                          }}
                          onError={() => setImageError(true)}
                        />
                      )}

                      {isImage && imageError && (
                        <div style={{ textAlign: "center", padding: "20px" }}>
                          <p>Unable to load image.</p>
                          <a
                            href={link}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-download"
                          >
                            Open in New Tab
                          </a>
                        </div>
                      )}

                      {isPpt && link && (
                        <iframe
                          title={name}
                          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(link)}`}
                          style={{
                            width: "100%",
                            height: "70dvh",
                            border: "none",
                          }}
                          onError={() => setPptViewer("google")}
                        />
                      )}

                      {pptViewer === "google" && link && (
                        <iframe
                          title={name}
                          src={`https://docs.google.com/gview?url=${encodeURIComponent(link)}&embedded=true`}
                          style={{
                            width: "100%",
                            height: "70dvh",
                            border: "none",
                          }}
                          onError={() => setPptViewer("failed")}
                        />
                      )}
                      {pptViewer === "failed" && (
                        <div style={{ textAlign: "center", padding: "20px" }}>
                          <p>Preview not available.</p>
                          <a
                            href={link}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-download"
                          >
                            Download
                          </a>
                        </div>
                      )}

                      {!link && !modalLoading && (
                        <div>No content URL found.</div>
                      )}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OverallMaterials;
